import { useEffect } from "react";

// ---------- Telegram WebApp types ----------
type TgThemeParams = {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
};

type TgBackButton = {
  isVisible: boolean;
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
};

type TgMainButton = {
  text: string;
  isVisible: boolean;
  isActive: boolean;
  isProgressVisible: boolean;
  show(): void;
  hide(): void;
  enable(): void;
  disable(): void;
  showProgress(leaveActive?: boolean): void;
  hideProgress(): void;
  setText(text: string): void;
  setParams(params: {
    text?: string;
    color?: string;
    text_color?: string;
    is_active?: boolean;
    is_visible?: boolean;
  }): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
};

type TgHapticFeedback = {
  impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
  notificationOccurred(type: "error" | "success" | "warning"): void;
  selectionChanged(): void;
};

export type TelegramWebApp = {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name?: string;
      last_name?: string;
      username?: string;
      language_code?: string;
    };
    auth_date?: number;
    hash?: string;
  };
  version: string;
  platform: string;
  colorScheme: "light" | "dark";
  themeParams: TgThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  headerColor: string;
  backgroundColor: string;
  BackButton: TgBackButton;
  MainButton: TgMainButton;
  HapticFeedback: TgHapticFeedback;
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  enableClosingConfirmation(): void;
  disableClosingConfirmation(): void;
};

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

// ---------- helpers ----------
export function getTg(): TelegramWebApp | null {
  const tg = window.Telegram?.WebApp;
  // initData пустой — значит просто скрипт подключён в браузере, но не в TG
  if (!tg || !tg.initData) return null;
  return tg;
}

export function isInTelegram(): boolean {
  return getTg() !== null;
}

// ---------- BackButton hook ----------
export function useTgBackButton(onClick: () => void, visible: boolean = true) {
  useEffect(() => {
    const tg = getTg();
    if (!tg) return;

    const handler = () => {
      try {
        tg.HapticFeedback.impactOccurred("light");
      } catch {
        // silent: Telegram WebApp API absent or read-only
      }
      onClick();
    };

    if (visible) {
      tg.BackButton.onClick(handler);
      tg.BackButton.show();
    } else {
      tg.BackButton.hide();
    }

    return () => {
      tg.BackButton.offClick(handler);
      tg.BackButton.hide();
    };
  }, [onClick, visible]);
}

// ---------- MainButton hook ----------
export type MainButtonOptions = {
  text: string;
  onClick: () => void;
  visible?: boolean;
  active?: boolean;
  progress?: boolean;
};

export function useTgMainButton(opts: MainButtonOptions | null) {
  useEffect(() => {
    const tg = getTg();
    if (!tg) return;
    if (!opts) {
      tg.MainButton.hide();
      return;
    }

    const handler = () => {
      try {
        tg.HapticFeedback.impactOccurred("medium");
      } catch {
        // silent: Telegram WebApp API absent or read-only
      }
      opts.onClick();
    };

    tg.MainButton.setParams({
      text: opts.text,
      color: "var(--accent-text)",
      text_color: "#ffffff",
      is_active: opts.active !== false,
      is_visible: opts.visible !== false,
    });

    if (opts.progress) {
      tg.MainButton.showProgress(true);
    } else {
      tg.MainButton.hideProgress();
    }

    tg.MainButton.onClick(handler);

    return () => {
      tg.MainButton.offClick(handler);
      tg.MainButton.hide();
      tg.MainButton.hideProgress();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts?.text, opts?.onClick, opts?.visible, opts?.active, opts?.progress]);
}
